import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${SITE_URL}/about#webpage`,
      "url": `${SITE_URL}/about`,
      "name": "About GK Vertex Consulting | Our Story, Values & Process",
      "description": "Learn about GK Vertex Consulting — our story, core values, and the streamlined process we use to build premium, high-converting websites for ambitious businesses.",
      "inLanguage": "en-US",
      "isPartOf": { "@id": `${SITE_URL}/#website` },
      "dateModified": "2026-04-05",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "About", "item": `${SITE_URL}/about` }
        ]
      }
    },
    {
      "@type": "AboutPage",
      "url": `${SITE_URL}/about`,
      "name": "About GK Vertex Consulting",
      "description": "GK Vertex Consulting was born from a simple belief: every business deserves a website that actually works. We build custom digital experiences that position our clients as the obvious choice in their market.",
      "publisher": {
        "@type": "Organization",
        "name": "GK Vertex Consulting",
        "url": SITE_URL,
        "email": "gkvertexconsulting@gmail.com",
        "logo": {
          "@type": "ImageObject",
          "url": `${SITE_URL}/favicon.ico`
        }
      }
    },
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": "GK Vertex Consulting",
      "url": SITE_URL,
      "email": "gkvertexconsulting@gmail.com",
      "description": "A lean, driven web design and digital marketing agency committed to building custom, high-converting websites for ambitious businesses.",
      "foundingDate": "2024",
      "areaServed": "Worldwide",
      "serviceType": ["Web Design", "Digital Marketing", "SEO", "Lead Generation"],
      "slogan": "Built on Ambition, Driven by Results",
      "knowsAbout": ["Custom Web Design", "SEO Optimization", "Digital Marketing", "Lead Generation", "Conversion Rate Optimization"]
    },
    {
      "@type": "ItemList",
      "name": "Our Core Values",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Quality First", "description": "We never ship work we're not proud of. Every pixel, every line of code, every word of copy is crafted with intention and care." },
        { "@type": "ListItem", "position": 2, "name": "Results Driven", "description": "We measure success by your growth — leads generated, conversions improved, revenue increased." },
        { "@type": "ListItem", "position": 3, "name": "Always Innovating", "description": "The digital landscape evolves fast. We stay ahead of trends so your website never feels dated." },
        { "@type": "ListItem", "position": 4, "name": "Client Focused", "description": "You work directly with us, get real updates, and have a direct line to the people building your project." }
      ]
    }
  ]
};

const values = [
  {
    icon: "ri-heart-pulse-line",
    title: "Quality First",
    description: "We never ship work we're not proud of. Every pixel, every line of code, every word of copy is crafted with intention and care.",
  },
  {
    icon: "ri-bar-chart-grouped-line",
    title: "Results Driven",
    description: "We measure success by your growth — leads generated, conversions improved, revenue increased. That's the only metric that matters.",
  },
  {
    icon: "ri-lightbulb-flash-line",
    title: "Always Innovating",
    description: "The digital landscape evolves fast. We stay ahead of trends so your website never feels dated or behind the competition.",
  },
  {
    icon: "ri-user-star-line",
    title: "Client Focused",
    description: "You're not a ticket number. You work directly with us, get real updates, and have a direct line to the people building your project.",
  },
];

const reasons = [
  {
    icon: "ri-focus-3-line",
    title: "Built for Conversions",
    description: "Every design decision is made with one goal: turning your visitors into paying clients. We don't just make things look good — we make them work.",
    stat: "3x",
    statLabel: "Avg. Lead Growth",
  },
  {
    icon: "ri-timer-flash-line",
    title: "Fast Turnaround",
    description: "We move fast without cutting corners. From kickoff to launch, we keep things efficient so you start seeing results as soon as possible.",
    stat: "10",
    statLabel: "Days Avg. Delivery",
  },
  {
    icon: "ri-award-line",
    title: "Modern, Premium Design",
    description: "No templates. No shortcuts. Every website is custom-crafted to reflect your brand and position you as the premium choice in your market.",
    stat: "100%",
    statLabel: "Custom Built",
  },
  {
    icon: "ri-user-heart-line",
    title: "Personalized Service",
    description: "You work directly with us — not a junior team or outsourced developers. You get our full attention, expertise, and commitment.",
    stat: "98%",
    statLabel: "Client Satisfaction",
  },
];

const steps = [
  {
    number: "01",
    title: "Consultation",
    subtitle: "Discovery & Strategy",
    description: "We start with a deep-dive call to understand your business, goals, and target audience. We map out a clear strategy before a single pixel is designed.",
    deliverables: ["Business Goals Audit", "Competitor Analysis", "Project Roadmap", "Timeline & Pricing"],
    duration: "1–2 Days",
    icon: "ri-discuss-line",
  },
  {
    number: "02",
    title: "Design & Build",
    subtitle: "Creation Phase",
    description: "Our team crafts your custom design and builds it with clean, fast code. You get regular updates and can provide feedback throughout the process.",
    deliverables: ["Custom UI Design", "Responsive Development", "Content Integration", "Performance Tuning"],
    duration: "7–10 Days",
    icon: "ri-tools-line",
  },
  {
    number: "03",
    title: "Launch & Optimize",
    subtitle: "Go Live & Grow",
    description: "We launch your site, set up analytics, and monitor performance. Then we optimize based on real data to keep improving your results over time.",
    deliverables: ["Live Deployment", "Analytics Setup", "SEO Configuration", "Ongoing Support"],
    duration: "1–2 Days",
    icon: "ri-rocket-line",
  },
];

const stats = [
  { value: "98%", label: "Client Satisfaction" },
  { value: "3x", label: "Avg. Lead Growth" },
  { value: "100%", label: "Custom Built" },
];

export default function AboutPage() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "about-jsonld";
    script.text = JSON.stringify(aboutJsonLd);
    document.head.appendChild(script);
    document.title = "About GK Vertex Consulting | Our Story, Values & Process";
    const metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) metaDesc.setAttribute("content", "Learn about GK Vertex Consulting — our story, core values, and the streamlined process we use to build premium, high-converting websites for ambitious businesses.");
    return () => {
      const el = document.getElementById("about-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050810]">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-28 overflow-hidden bg-[#050810]">
          <div className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: "linear-gradient(rgba(212,175,55,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.3) 1px, transparent 1px)",
              backgroundSize: "80px 80px"
            }}
          ></div>
          <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-[#d4af37]/6 blur-[120px] pointer-events-none"></div>
          <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] rounded-full bg-[#1a2a5e]/20 blur-[100px] pointer-events-none"></div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-2 mb-6">
              <Link to="/" className="text-white/40 text-sm hover:text-white/70 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-white/20 text-sm"></i>
              <span className="text-[#d4af37] text-sm">About Us</span>
            </div>
            <div className="max-w-3xl">
              <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-4 block">About GK Vertex</span>
              <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
                Built on Ambition,<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d060]">Driven by Results</span>
              </h1>
              <p className="text-white/50 text-lg leading-relaxed max-w-2xl">
                We're a lean, driven agency that combines strategic thinking with premium design and technical excellence. Every project we take on gets our full commitment — no exceptions.
              </p>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-28 bg-[#080b18]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Visual */}
              <div className="relative">
                <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#0c0f1c] to-[#080b18] border border-white/5 p-10 min-h-[420px] flex flex-col justify-between">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 p-6 rounded-xl bg-[#d4af37]/8 border border-[#d4af37]/15">
                      <i className="ri-code-s-slash-line text-[#d4af37] text-3xl mb-3 block"></i>
                      <div className="text-white font-bold text-sm">Clean Code</div>
                      <div className="text-white/40 text-xs mt-1">Built to perform</div>
                    </div>
                    <div className="flex-1 p-6 rounded-xl bg-white/3 border border-white/8">
                      <i className="ri-palette-line text-white/60 text-3xl mb-3 block"></i>
                      <div className="text-white font-bold text-sm">Premium Design</div>
                      <div className="text-white/40 text-xs mt-1">No templates, ever</div>
                    </div>
                  </div>
                  <div className="text-center py-8">
                    <div className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d060] mb-2">100%</div>
                    <div className="text-white/50 text-sm tracking-wide">Committed to every project</div>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 p-6 rounded-xl bg-white/3 border border-white/8">
                      <i className="ri-line-chart-line text-white/60 text-3xl mb-3 block"></i>
                      <div className="text-white font-bold text-sm">Results Driven</div>
                      <div className="text-white/40 text-xs mt-1">Measurable growth</div>
                    </div>
                    <div className="flex-1 p-6 rounded-xl bg-[#d4af37]/8 border border-[#d4af37]/15">
                      <i className="ri-user-star-line text-[#d4af37] text-3xl mb-3 block"></i>
                      <div className="text-white font-bold text-sm">Client First</div>
                      <div className="text-white/40 text-xs mt-1">Always in your corner</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -left-4 bg-[#d4af37] rounded-2xl p-4 hidden md:flex items-center gap-3">
                  <i className="ri-shield-check-line text-[#050810] text-xl"></i>
                  <div>
                    <div className="text-[#050810] font-black text-sm">100% Custom</div>
                    <div className="text-[#050810]/60 text-xs">No templates, ever</div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-4 block">Our Story</span>
                <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-6">
                  Why We Started<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d060]">GK Vertex</span>
                </h2>
                <div className="space-y-5 text-white/60 text-base leading-relaxed">
                  <p>
                    GK Vertex Consulting was born from a simple belief: <strong className="text-white">every business deserves a website that actually works</strong>. Not just one that looks good — one that generates real leads, builds trust, and drives growth.
                  </p>
                  <p>
                    We're a lean, driven agency that combines strategic thinking with premium design and technical excellence. We don't do cookie-cutter. We don't do templates. We build custom digital experiences that position our clients as the obvious choice in their market.
                  </p>
                  <p>
                    As a growing agency, we're selective about who we work with — because we're fully committed to every project we take on. When you work with us, you get our best. Every time.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-10">
                  {values.map((val) => (
                    <div key={val.title} className="flex items-center gap-3 p-4 rounded-xl border border-white/5 bg-[#0c0f1c]">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#d4af37]/10">
                        <i className={`${val.icon} text-[#d4af37] text-base`}></i>
                      </div>
                      <span className="text-white/70 text-sm font-medium">{val.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Deep Dive */}
        <section className="py-28 bg-[#050810]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-16">
              <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-4 block">Our Values</span>
              <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
                What We Stand For
              </h2>
              <p className="text-white/50 text-base max-w-lg mx-auto">
                These aren't just words on a page — they're the principles that guide every decision we make.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {values.map((val) => (
                <div key={val.title} className="group flex gap-6 p-8 rounded-2xl border border-white/5 bg-[#0c0f1c] hover:border-[#d4af37]/20 transition-all duration-300">
                  <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 group-hover:bg-[#d4af37]/20 transition-colors duration-300">
                    <i className={`${val.icon} text-[#d4af37] text-2xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-2">{val.title}</h3>
                    <p className="text-white/50 text-sm leading-relaxed">{val.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section className="py-28 bg-[#080b18] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#d4af37]/5 blur-3xl pointer-events-none"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-16">
              <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-4 block">Why GK Vertex</span>
              <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
                The Agency That Actually<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d060]">Delivers Results</span>
              </h2>
              <p className="text-white/50 text-base max-w-lg mx-auto leading-relaxed">
                We're not just building websites — we're engineering digital experiences that convert visitors into loyal customers.
              </p>
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden mb-16">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-[#080b18] px-8 py-8 text-center">
                  <div className="text-3xl md:text-4xl font-black text-[#d4af37] mb-2">{stat.value}</div>
                  <div className="text-white/40 text-xs tracking-wide">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reasons.map((reason) => (
                <div key={reason.title} className="group p-8 rounded-2xl border border-white/5 bg-[#0c0f1c] hover:border-[#d4af37]/20 transition-all duration-300">
                  <div className="flex items-start gap-5 mb-6">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 group-hover:bg-[#d4af37]/20 transition-colors duration-300">
                      <i className={`${reason.icon} text-[#d4af37] text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-base mb-2">{reason.title}</h3>
                      <p className="text-white/50 text-sm leading-relaxed">{reason.description}</p>
                    </div>
                  </div>
                  <div className="pt-5 border-t border-white/5 flex items-center gap-3">
                    <span className="text-3xl font-black text-[#d4af37]">{reason.stat}</span>
                    <span className="text-white/40 text-xs">{reason.statLabel}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section className="py-28 bg-[#050810]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-20">
              <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-4 block">How It Works</span>
              <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
                A Simple, Streamlined<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d060]">Process That Delivers</span>
              </h2>
              <p className="text-white/50 text-base max-w-md mx-auto">
                A clear, efficient process designed to get you results fast — without the back-and-forth chaos.
              </p>
            </div>

            <div className="relative">
              <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#d4af37]/20 to-transparent -translate-x-1/2"></div>
              <div className="space-y-12">
                {steps.map((step, index) => (
                  <div
                    key={step.number}
                    className={`flex flex-col lg:flex-row items-start lg:items-center gap-8 ${index % 2 === 1 ? "lg:flex-row-reverse" : ""}`}
                  >
                    <div className="flex-1 lg:max-w-[calc(50%-60px)]">
                      <div className={`p-8 rounded-2xl border border-white/5 bg-[#0c0f1c] hover:border-[#d4af37]/20 transition-all duration-300 ${index % 2 === 1 ? "lg:ml-auto" : ""}`}>
                        <div className="flex items-center gap-3 mb-5">
                          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20">
                            <i className={`${step.icon} text-[#d4af37] text-lg`}></i>
                          </div>
                          <span className="text-white/30 text-xs font-semibold tracking-widest uppercase">{step.duration}</span>
                        </div>
                        <h3 className="text-white font-black text-xl mb-1">{step.title}</h3>
                        <div className="text-[#d4af37] text-sm font-medium mb-4">{step.subtitle}</div>
                        <p className="text-white/50 text-sm leading-relaxed mb-6">{step.description}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {step.deliverables.map((item) => (
                            <div key={item} className="flex items-center gap-2 text-white/40 text-xs">
                              <i className="ri-check-line text-[#d4af37]"></i>
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="hidden lg:flex flex-shrink-0 w-24 h-24 items-center justify-center rounded-full border border-[#d4af37]/20 bg-[#0c0f1c] relative z-10">
                      <span className="text-[#d4af37] font-black text-2xl">{step.number}</span>
                    </div>
                    <div className="flex-1 hidden lg:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-[#080b18]">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-4 block">Ready to Start?</span>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-6">
              Let's Build Something<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d060]">Great Together</span>
            </h2>
            <p className="text-white/50 text-base mb-10 max-w-lg mx-auto">
              Ready to grow your business online? Let's talk about what we can build together.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#d4af37] text-[#050810] font-bold text-sm tracking-wide hover:bg-[#e8c84a] transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-send-line"></i>
                Get in Touch
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/15 text-white text-sm font-semibold hover:border-[#d4af37]/40 hover:text-[#d4af37] transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                View Our Services
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
